// assets/component-drawer.js
// Handles open/close for all drawers (centered modals + pin-right drawers).
// Uses event delegation — works with dynamically rendered content.
//
// Markup contract:
//   Trigger:  data-drawer-target="<drawer-id>"
//   Drawer:   id="<drawer-id>" class="drawer" role="dialog" aria-modal="true"
//   Close:    class="drawer__close" (inside the drawer)
//   Overlay:  class="drawer__overlay" (inside the drawer)
//
// Optional: data-drawer-tab="<index>" on trigger to open a specific tab

(function () {
  'use strict';

  // Tracks which element was focused before each drawer opened, for restoration on close
  const focusTriggers = new WeakMap();

  // position: fixed + top: -scrollY keeps the visual slice put.
  // 'instant' on restore beats the global scroll-behavior: smooth.
  let savedScrollY = 0;
  let isLocked = false;

  function lockScroll() {
    if (isLocked) return;
    isLocked = true;
    savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  function unlockScroll() {
    if (!isLocked) return;
    isLocked = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo({ top: savedScrollY, left: 0, behavior: 'instant' });
  }

  // Visible, interactive elements eligible for focus
  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(drawer) {
    return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null
    );
  }

  // Open a drawer by ID, optionally switching to a tab
  function open(id, tabIndex) {
    const drawer = document.getElementById(id);
    if (!drawer) return;

    // Save focus origin so we can restore it when this drawer closes
    focusTriggers.set(drawer, document.activeElement);

    // Close any currently open drawers before opening a new one
    document.querySelectorAll('.drawer.is-open').forEach((openDrawer) => {
      if (openDrawer !== drawer) close(openDrawer);
    });

    drawer.classList.add('is-open');
    lockScroll();

    // Switch to specific tab if requested
    if (tabIndex != null && window.wfhTabs) {
      const tabContainer = drawer.querySelector('[data-tabs]');
      if (tabContainer) {
        window.wfhTabs.switchTab(tabContainer, tabIndex);
      }
    }

    // Move focus into the drawer
    const focusTarget = getFocusable(drawer)[0];
    if (focusTarget) focusTarget.focus();

    drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));

    // Trigger Okendo to scan for new star rating widgets
    const okeApi = window.okeWidgetApi || window.oke;
    if (okeApi) Promise.resolve(okeApi.initWidget()).catch(() => {});
  }

  // Close a specific drawer element
  function close(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;

    drawer.classList.remove('is-open');
    drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    unlockScroll();

    // Return focus to wherever it was before this drawer opened
    const origin = focusTriggers.get(drawer);
    if (origin && typeof origin.focus === 'function') origin.focus();
    focusTriggers.delete(drawer);
  }

  // Close all open drawers
  function closeAll() {
    document.querySelectorAll('.drawer.is-open').forEach(close);
  }

  // Delegated click handler
  document.addEventListener('click', (e) => {
    // Open trigger
    const trigger = e.target.closest('[data-drawer-target]');
    if (trigger) {
      e.preventDefault();
      open(trigger.dataset.drawerTarget, trigger.dataset.drawerTab);
      return;
    }

    // Close button or overlay
    const drawer = e.target.closest('.drawer');
    if (!drawer) return;

    if (
      e.target.closest('.drawer__close') ||
      e.target.closest('.drawer__overlay')
    ) {
      close(drawer);
    }
  });

  // ESC closes all open drawers; Tab is trapped inside the open drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAll();
      return;
    }

    if (e.key !== 'Tab') return;

    const openDrawer = document.querySelector('.drawer.is-open');
    if (!openDrawer) return;

    const focusable = getFocusable(openDrawer);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      // Shift+Tab from first element wraps to last
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab from last element wraps to first
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Public API for programmatic access
  window.wfhDrawer = { open, close, closeAll, lockScroll, unlockScroll };
})();
